import { useState, useEffect } from "react";
import { Sparkle } from "@phosphor-icons/react";

const SECTIONS = [
  {
    key: "company", title: "Company Information", fields: [
      { k: "company_name", l: "Company name", w: "full" },
      { k: "website", l: "Website", w: "half" },
      { k: "registration_number", l: "Registration number", w: "half" },
      { k: "country", l: "Country", w: "half" },
      { k: "industry", l: "Industry", w: "half" },
      { k: "founding_date", l: "Founding date", w: "half", type: "date" },
      { k: "company_stage", l: "Company stage", w: "half", type: "select", options: ["Idea", "Pre-Seed", "Seed", "Series A", "Series B", "Series C+", "Growth", "Established"] },
      { k: "mission", l: "Mission", w: "full", type: "textarea" },
      { k: "vision", l: "Vision", w: "full", type: "textarea" },
      { k: "business_description", l: "Business description", w: "full", type: "textarea", rows: 4 },
    ]
  },
  {
    key: "product", title: "Product & Value Proposition", fields: [
      { k: "products", l: "Products", w: "half", type: "textarea" },
      { k: "services", l: "Services", w: "half", type: "textarea" },
      { k: "problem", l: "Problem being solved", w: "full", type: "textarea" },
      { k: "solution", l: "Solution", w: "full", type: "textarea" },
      { k: "value_proposition", l: "Value proposition", w: "full", type: "textarea" },
      { k: "target_customers", l: "Target customers", w: "full", type: "textarea" },
    ]
  },
  {
    key: "market", title: "Market & Competition", fields: [
      { k: "market_opportunity", l: "Market opportunity (TAM/SAM/SOM)", w: "full", type: "textarea", rows: 4 },
      { k: "competitive_landscape", l: "Competitive landscape", w: "full", type: "textarea", rows: 3 },
    ]
  },
  {
    key: "business_model", title: "Business Model", fields: [
      { k: "business_model", l: "Business model", w: "full", type: "textarea" },
      { k: "pricing", l: "Pricing", w: "half", type: "textarea" },
      { k: "revenue_streams", l: "Revenue streams", w: "half", type: "textarea" },
    ]
  },
  {
    key: "traction", title: "Traction", fields: [
      { k: "traction", l: "Traction narrative", w: "full", type: "textarea", rows: 3 },
      { k: "customers", l: "Key customers", w: "half", type: "textarea" },
      { k: "partnerships", l: "Partnerships", w: "half", type: "textarea" },
    ]
  },
  {
    key: "financials", title: "Financial Information", fields: [
      { k: "historical_revenue", l: "Historical revenue", w: "half", type: "textarea" },
      { k: "projected_revenue", l: "Projected revenue", w: "half", type: "textarea" },
      { k: "gross_margin", l: "Gross margin", w: "third" },
      { k: "ebitda", l: "EBITDA", w: "third" },
      { k: "cash_position", l: "Cash position", w: "third" },
      { k: "funding_history", l: "Funding history", w: "full", type: "textarea" },
      { k: "funding_required", l: "Funding required (this round)", w: "half" },
      { k: "use_of_funds", l: "Use of funds", w: "half", type: "textarea" },
    ]
  },
  {
    key: "roadmap", title: "Roadmap & Technology", fields: [
      { k: "roadmap", l: "Roadmap", w: "full", type: "textarea", rows: 3 },
      { k: "technology_stack", l: "Technology stack", w: "full", type: "textarea" },
    ]
  },
  {
    key: "risks", title: "Risks & Mitigation", fields: [
      { k: "risks", l: "Key risks", w: "full", type: "textarea", rows: 3 },
      { k: "mitigation_strategy", l: "Mitigation strategy", w: "full", type: "textarea", rows: 3 },
    ]
  },
  {
    key: "esg", title: "ESG & Impact", fields: [
      { k: "esg", l: "ESG framework", w: "full", type: "textarea" },
      { k: "impact", l: "Social / environmental impact", w: "full", type: "textarea" },
    ]
  },
  {
    key: "contact", title: "Contact Details", fields: [
      { k: "contact_email", l: "Contact email", w: "half", type: "email" },
      { k: "contact_phone", l: "Contact phone", w: "half" },
    ]
  },
];

export default function InvestmentProfileTab({ profile, onSave, onAnalyse, busy }) {
  const [form, setForm] = useState(profile || {});
  const [dirty, setDirty] = useState(false);
  const [openSection, setOpenSection] = useState("company");

  useEffect(() => { setForm(profile || {}); setDirty(false); }, [profile]);

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const wClass = (w) => w === "full" ? "col-span-6" : w === "half" ? "col-span-6 md:col-span-3" : w === "third" ? "col-span-6 md:col-span-2" : "col-span-6";

  const completion = () => {
    const all = SECTIONS.flatMap((s) => s.fields.map((f) => f.k));
    const filled = all.filter((k) => {
      const v = form[k]; return v && (typeof v === "string" ? v.trim().length > 0 : true);
    });
    return Math.round((filled.length / all.length) * 100);
  };
  const pct = completion();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="idoc-tab-profile-body">
      <aside className="lg:col-span-1 lg:sticky lg:top-6 self-start">
        <div className="bg-card border border-border rounded-2xl p-5 card-elev">
          <div className="text-overline text-muted-foreground mb-3">Profile completion</div>
          <div className="text-3xl font-display tracking-tight text-primary">{pct}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-6 space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setOpenSection(s.key)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${openSection === s.key ? "bg-teal-soft text-teal font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
                data-testid={`ip-nav-${s.key}`}
              >
                {s.title}
              </button>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-border space-y-2">
            <button
              onClick={() => { onSave(form); setDirty(false); }}
              disabled={!dirty}
              className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="ip-save-btn"
            >
              {dirty ? "Save profile" : "Saved"}
            </button>
            <button
              onClick={onAnalyse}
              disabled={busy}
              className="w-full bg-foreground text-background rounded-lg py-2.5 text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="ip-analyse-btn"
            >
              <Sparkle size={14} weight="bold" /> Run AI analysis
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:col-span-3 space-y-6">
        {SECTIONS.map((s) => (
          <div
            key={s.key}
            className={`bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev transition-all ${openSection === s.key ? "" : "opacity-90"}`}
            id={`section-${s.key}`}
            data-testid={`ip-section-${s.key}`}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-overline text-primary">Section</div>
                <div className="font-display text-2xl tracking-tight">{s.title}</div>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-4">
              {s.fields.map((f) => (
                <div key={f.k} className={wClass(f.w)}>
                  <label className="text-overline text-muted-foreground block mb-2">{f.l}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      value={form[f.k] ?? ""}
                      onChange={(e) => setField(f.k, e.target.value)}
                      rows={f.rows || 2}
                      className="w-full bg-card border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm resize-y"
                      data-testid={`ip-field-${f.k}`}
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={form[f.k] ?? ""}
                      onChange={(e) => setField(f.k, e.target.value)}
                      className="w-full bg-card border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      data-testid={`ip-field-${f.k}`}
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={f.type || "text"}
                      value={form[f.k] ?? ""}
                      onChange={(e) => setField(f.k, e.target.value)}
                      className="w-full bg-card border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      data-testid={`ip-field-${f.k}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
